import {
  computeCombinedTotals,
  findPartnerLinkedSource,
  isPartnerSharedRecipient,
} from '@/components/funding-sources-coordination/partner-funding-utils';
import type { AllocationOption } from '@/components/funding-sources-coordination/partner-allocation-section';
import { useFeatureFlags } from '@/contexts/FeatureFlagsContext';
import {
  ASSET_TYPE_TO_ACCOUNT_TYPE,
  SELF_FUNDING_ASSET_TYPES,
  getCalcFormulaInfo,
  isIncomeStreamAssetTypeId,
  type AssetTypeId,
} from '@/features/guided-financial-planning/config';
import { usePartnerLockedFundingSources } from '@/features/guided-financial-planning/hooks/use-partner-locked-funding-sources';
import { useVehiclePartnerActions } from '@/hooks/use-vehicle-partner-actions';
import { putMultipleFundingSourceForClientByClientIdRequest } from '@client/model';
import {
  selectClient,
  selectSession,
  selectSessionAdvisor,
} from '@client/model/selectors';
import { useSelfFundingProjectedValue } from '@client/hooks/useSelfFundingProjectedValue';
import {
  SessionType,
  calculateAgeFromBirthDate,
  carePhaseDefList,
  isNullOrUndefined,
} from '@shared';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import { useCareHasStarted } from '@/shared/hooks/use-care-has-started';

import {
  applyAssetTypeChange,
  buildSelfFundingSource,
  deriveAllocationOption,
  deriveFieldLabels,
  deriveRequiredKeys,
  deriveUsedAccountTypes,
  getInitialAssetType,
  getInitialYearlyContribution,
  validateIncomeReferenceAge,
} from '../utils';
import type { CarePhaseYears } from '@/features/guided-financial-planning/components/year-select';
import { CURRENT_YEAR } from '@/features/guided-financial-planning/components/year-select';

type SelfFundingKey = keyof SelfFundingSource;

interface UseSelfFundingModalFormArgs {
  open: boolean;
  setOpen: (open: boolean) => void;
  fundingSourceId?: string;
}

/**
 * Owns all form state, validation, derived values, and handlers for the
 * self-funding details modal. The component using this hook is reduced to
 * pure rendering — all business logic lives here or in the pure utilities
 * under `../utils/`.
 */
export function useSelfFundingModalForm({
  open,
  setOpen,
  fundingSourceId,
}: UseSelfFundingModalFormArgs) {
  const client = useSelector(selectClient);
  const { sessionType } = useSelector(selectSession);
  const { advisorId } = useSelector(selectSessionAdvisor);
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const { partnerFirstName, partnerClientId, saveWithPartnerSync, handleUnlink } =
    useVehiclePartnerActions();
  const { flags } = useFeatureFlags();

  const {
    clientId,
    multipleFundingSources,
    intakeSurvey: { clientHasStartedLtc, clientBirthDate },
    appliedInferenceSet: { ltcAtYear, yearsTillLtc, ltcAtAge },
    allPhaseCosts,
  } = client;

  const careHasStarted = useCareHasStarted();
  const currentAge = clientBirthDate ? calculateAgeFromBirthDate(clientBirthDate) : null;

  // ── Source lookup / fallback empty source ─────────────────────────────────
  const selfFunding =
    fundingSourceId && multipleFundingSources.selfFunding[fundingSourceId]
      ? multipleFundingSources.selfFunding[fundingSourceId]
      : ({
          hasSelfFunding: false,
          existingAssetsValue: null,
          annualRateOfReturn: null,
          contributionStartYear: new Date().getFullYear(),
          contributionEndYear: ltcAtYear,
          monthlyContributionAmount: null,
          gainsTaxRate: null,
          monthlyIncome: null,
          contributionMonthCount: 0,
        } as SelfFundingSource);

  // ── Recipient detection (canonical + session fallback) ────────────────────
  const { partnerSharedFundingSourceIds } = usePartnerLockedFundingSources();
  const isReadOnlyRecipient =
    isPartnerSharedRecipient(selfFunding as SelfFundingSource | undefined, clientId) ||
    (!!fundingSourceId && partnerSharedFundingSourceIds.has(fundingSourceId));

  const usedAccountTypes = useMemo(
    () =>
      deriveUsedAccountTypes({
        selfFunding: multipleFundingSources.selfFunding,
        currentFundingSourceId: fundingSourceId,
        partnerSharedFundingSourceIds,
      }),
    [multipleFundingSources.selfFunding, fundingSourceId, partnerSharedFundingSourceIds],
  );

  const isAdvisorSession =
    sessionType === SessionType.advisor || sessionType === SessionType.wizard;

  const isExistingMirror = selfFunding?.partnerAction === 'mirrored';
  const existingSplitRatio = selfFunding?.splitRatio ?? null;

  const partnerLinkedSource =
    isExistingMirror && selfFunding?.fundingSourceGroupId
      ? findPartnerLinkedSource(
          client.mutableClientPartner?.multipleFundingSources?.selfFunding,
          selfFunding.fundingSourceGroupId,
        )?.[1]
      : undefined;

  // Recipients see their own portion only; originators see the combined total.
  const combinedTotals = isReadOnlyRecipient
    ? {}
    : computeCombinedTotals(selfFunding, partnerLinkedSource);

  const initialSelfFundingValue: SelfFundingSource = {
    ...selfFunding,
    existingAssetsValue:
      combinedTotals.existingAssetsValue ?? selfFunding?.existingAssetsValue ?? null,
    monthlyContributionAmount:
      combinedTotals.monthlyContributionAmount ??
      selfFunding?.monthlyContributionAmount ??
      (careHasStarted ? 0 : null),
    annualRateOfReturn: selfFunding?.annualRateOfReturn ?? (careHasStarted ? 0 : null),
    gainsTaxRate: selfFunding?.gainsTaxRate ?? (careHasStarted ? 0 : null),
    monthlyIncome: combinedTotals.monthlyIncome ?? selfFunding?.monthlyIncome ?? null,
    drawdownAmount: combinedTotals.drawdownAmount ?? selfFunding?.drawdownAmount ?? null,
    ...(isAdvisorSession && { addedByAdvisorId: advisorId }),
  };

  // ── Form state ────────────────────────────────────────────────────────────
  const [selfFundingState, setSelfFundingState] =
    useState<SelfFundingSource>(initialSelfFundingValue);
  const [hasContributionStopToggle, setHasContributionStopToggle] = useState(
    () => initialSelfFundingValue.contributionStopYear != null,
  );
  const [formErrors, setFormErrors] = useState({} as Record<SelfFundingKey, string>);
  const [formHasBeenSubmitted, setFormHasBeenSubmitted] = useState(false);
  const [initialSelfFundingState, setInitialSelfFundingState] =
    useState<SelfFundingSource>(initialSelfFundingValue);
  const [showWarning, setShowWarning] = useState(false);
  const [mirrorChecked, setMirrorChecked] = useState(false);
  const [allocationOption, setAllocationOption] = useState<AllocationOption>('mirror');
  const [customClientPercent, setCustomClientPercent] = useState(50);
  const [showFormulaSheet, setShowFormulaSheet] = useState(false);

  const [selectedAssetType, setSelectedAssetType] = useState<AssetTypeId | 'other'>(() =>
    getInitialAssetType(selfFunding?.accountType),
  );
  const [yearlyContributionValue, setYearlyContributionValue] = useState<number | null>(() =>
    getInitialYearlyContribution(
      getInitialAssetType(selfFunding?.accountType),
      selfFunding?.monthlyContributionAmount,
    ),
  );

  // ── Derived flags ─────────────────────────────────────────────────────────
  const isIncomeStream =
    selectedAssetType !== 'other' && isIncomeStreamAssetTypeId(selectedAssetType);
  const isOther = selectedAssetType === 'other';
  const isSpecificAssetType = !isOther;

  const currentReferenceAge =
    typeof selfFundingState.incomeReferenceAge === 'number'
      ? selfFundingState.incomeReferenceAge
      : (ltcAtAge ?? null);

  const fieldLabels = deriveFieldLabels({
    selectedAssetType,
    isIncomeStream,
    currentReferenceAge,
    ltcAtAge,
  });

  const formulaAssetTypeId: AssetTypeId =
    selectedAssetType === 'other' ? 'brokerage' : selectedAssetType;
  const formulaInfo = getCalcFormulaInfo(formulaAssetTypeId);
  const assetLabel = isOther
    ? selfFundingState.selfFundingName || 'Self-funding'
    : (SELF_FUNDING_ASSET_TYPES.find(t => t.id === selectedAssetType)?.label ?? 'Self-funding');
  const readOnlyAccountLabel = isOther
    ? selfFunding?.selfFundingName || 'Self-funding'
    : (SELF_FUNDING_ASSET_TYPES.find(t => t.id === selectedAssetType)?.label ?? 'Self-funding');

  const carePhaseYears: CarePhaseYears = useMemo(() => {
    const startYears = client?.clientPhasePredictedStartYears;
    if (!startYears) return { earlyCare: null, moderateCare: null, fullCare: null };
    return {
      earlyCare: startYears[carePhaseDefList[0].value] ?? null,
      moderateCare: startYears[carePhaseDefList[1].value] ?? null,
      fullCare: startYears[carePhaseDefList[2].value] ?? null,
    };
  }, [client?.clientPhasePredictedStartYears]);

  const ltcEndYear = client?.clientPhasePredictedEndYears?.[carePhaseDefList[2].value] ?? null;
  const ltcEndAge =
    ltcEndYear != null && clientBirthDate
      ? ltcEndYear - new Date(clientBirthDate).getUTCFullYear()
      : ltcAtAge;

  const incomeReferenceAgeError = validateIncomeReferenceAge({
    value: selfFundingState.incomeReferenceAge,
    isIncomeStream,
    currentAge,
    ltcEndAge,
  });

  const requiredKeys = deriveRequiredKeys({
    isOther,
    isIncomeStream,
    clientHasStartedLtc: careHasStarted,
  });

  const showPartnerSection =
    Boolean(partnerFirstName) && Boolean(partnerClientId) && flags.partnerFundingSplit === true;
  const clientName = client.clientFirstName ?? 'Client';
  const totalAssets = selfFundingState.existingAssetsValue ?? 0;

  // ── Field-level handlers ──────────────────────────────────────────────────
  const handleChangeSelfFunding = (selfFundingKey: SelfFundingKey) => (value: number | null) => {
    setSelfFundingState(prev => ({ ...prev, [selfFundingKey]: value }));
  };

  function handleChangeSelfFundingName(value: string) {
    setSelfFundingState(prev => ({ ...prev, selfFundingName: value }));
  }

  function handleAssetTypeChange(value: string) {
    const newType = value as AssetTypeId | 'other';
    const prevType = selectedAssetType;
    setSelectedAssetType(newType);

    setSelfFundingState(prev => {
      const result = applyAssetTypeChange({ prev, prevType, newType, ltcAtAge });
      if (result.yearlyContributionUpdate !== undefined) {
        setYearlyContributionValue(result.yearlyContributionUpdate);
      }
      return result.state;
    });
  }

  function handleToggleContributionStop(checked: boolean) {
    setHasContributionStopToggle(checked);
    if (checked && selfFundingState.contributionStopYear == null) {
      setSelfFundingState(prev => ({ ...prev, contributionStopYear: CURRENT_YEAR + 5 }));
    } else if (!checked) {
      setSelfFundingState(prev => ({ ...prev, contributionStopYear: null }));
    }
  }

  function handleToggleDrawdown(checked: boolean) {
    setSelfFundingState(prev => ({
      ...prev,
      hasDrawdown: checked,
      ...(!checked && {
        drawdownStartAtYear: null,
        drawdownEndAtYear: null,
        drawdownAmount: null,
        drawdownAmountInterval: null,
      }),
      ...(checked &&
        !prev.drawdownAmountInterval && {
          drawdownAmountInterval: 'yearly',
        }),
    }));
  }

  function handleChangeYearlyContribution(value: number | null) {
    setYearlyContributionValue(value);
    setSelfFundingState(prev => ({
      ...prev,
      monthlyContributionAmount: value == null ? null : value / 12,
    }));
  }

  // ── Validation + submit ───────────────────────────────────────────────────
  function validateForm() {
    const errors = {} as Record<SelfFundingKey, string>;
    requiredKeys.forEach(key => {
      if (isNullOrUndefined(selfFundingState[key])) {
        errors[key] = 'Required Field';
      }
    });
    if (incomeReferenceAgeError) {
      errors.incomeReferenceAge = incomeReferenceAgeError;
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  }

  function handleClickSubmit() {
    setFormHasBeenSubmitted(true);
    if (!validateForm()) return;

    const updatedSelfFunding = buildSelfFundingSource({
      selfFundingState,
      selectedAssetType,
      isOther,
      isIncomeStream,
      isSpecificAssetType,
      yearlyContributionValue,
      ltcAtAge,
    });

    if (isExistingMirror && !mirrorChecked && fundingSourceId) {
      // Unchecking partner-sync on a mirrored source unlinks. Pass the edited
      // source plus an explicit owner-restore so handleUnlink uses the form's
      // combined totals rather than re-summing stale stored portions.
      handleUnlink(fundingSourceId, updatedSelfFunding, {
        existingAssetsValue: updatedSelfFunding.existingAssetsValue,
        monthlyContributionAmount: updatedSelfFunding.monthlyContributionAmount,
        monthlyIncome: updatedSelfFunding.monthlyIncome,
        drawdownAmount: updatedSelfFunding.drawdownAmount,
      });
    } else if (mirrorChecked && partnerClientId) {
      saveWithPartnerSync({
        fundingSourceId,
        source: updatedSelfFunding,
        isExistingMirror,
        allocationOption,
        clientPercent: customClientPercent,
      });
    } else {
      dispatch(
        putMultipleFundingSourceForClientByClientIdRequest({
          clientId,
          fundingSourceType: 'selfFunding',
          fundingSourceData: updatedSelfFunding,
          fundingSourceId: fundingSourceId ?? null,
        }),
      );
    }

    const newParams = new URLSearchParams(searchParams);
    newParams.set('downloadReport', 'true');
    setSearchParams(newParams, { replace: true });
    setOpen(false);
  }

  const formIsValid = Object.keys(formErrors).length === 0;
  const hasRequiredFieldsErrors = Object.keys(formErrors).some(key =>
    requiredKeys.includes(key as SelfFundingKey),
  );

  // ── Lifecycle: reset / close / unlink ─────────────────────────────────────
  function resetForm() {
    setSelfFundingState(selfFunding ?? null);
    setHasContributionStopToggle(selfFunding?.contributionStopYear != null);
    setFormErrors({} as Record<SelfFundingKey, string>);
    setMirrorChecked(isExistingMirror);
    setAllocationOption(deriveAllocationOption({ isExistingMirror, existingSplitRatio }));
    setCustomClientPercent(existingSplitRatio ?? 50);
    const initialType = getInitialAssetType(selfFunding?.accountType);
    setSelectedAssetType(initialType);
    setYearlyContributionValue(
      getInitialYearlyContribution(initialType, selfFunding?.monthlyContributionAmount),
    );
  }

  function onClose() {
    if (JSON.stringify(selfFundingState) !== JSON.stringify(initialSelfFundingState)) {
      setShowWarning(true);
    } else {
      setOpen(false);
      resetForm();
    }
  }

  function handleConfirmClose() {
    setShowWarning(false);
    setOpen(false);
    resetForm();
  }

  function handleCancelClose() {
    setShowWarning(false);
  }

  // ── Effects ───────────────────────────────────────────────────────────────
  // Re-run validation whenever a key field changes, but only after the user
  // has tried to submit at least once.
  useEffect(() => {
    if (formHasBeenSubmitted) {
      validateForm();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selfFundingState.annualRateOfReturn,
    selfFundingState.existingAssetsValue,
    selfFundingState.gainsTaxRate,
    selfFundingState.incomeReferenceAge,
    selectedAssetType,
  ]);

  // On open, rebuild the working state from the underlying source.
  useEffect(() => {
    if (!open) return;

    const openingState: SelfFundingSource = selfFunding
      ? {
          ...selfFunding,
          hasSelfFunding: true,
          existingAssetsValue:
            combinedTotals.existingAssetsValue ?? selfFunding.existingAssetsValue ?? null,
          monthlyContributionAmount:
            combinedTotals.monthlyContributionAmount ??
            selfFunding.monthlyContributionAmount ??
            null,
          monthlyIncome: combinedTotals.monthlyIncome ?? selfFunding.monthlyIncome ?? null,
          drawdownAmount: combinedTotals.drawdownAmount ?? selfFunding.drawdownAmount ?? null,
        }
      : ({ hasSelfFunding: false } as SelfFundingSource);

    const finalState = isAdvisorSession
      ? { ...openingState, addedByAdvisorId: advisorId }
      : openingState;

    setSelfFundingState(finalState);
    setHasContributionStopToggle(finalState.contributionStopYear != null);
    setInitialSelfFundingState(finalState);
    setFormHasBeenSubmitted(false);

    setMirrorChecked(isExistingMirror);
    setAllocationOption(deriveAllocationOption({ isExistingMirror, existingSplitRatio }));
    setCustomClientPercent(existingSplitRatio ?? 50);

    const initialType = getInitialAssetType(selfFunding?.accountType);
    setSelectedAssetType(initialType);
    setYearlyContributionValue(
      getInitialYearlyContribution(initialType, finalState.monthlyContributionAmount),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // ── Projected-value preview ───────────────────────────────────────────────
  const previewState: SelfFundingSource = {
    ...selfFundingState,
    accountType: isOther
      ? (selfFundingState.accountType ?? null)
      : ASSET_TYPE_TO_ACCOUNT_TYPE[selectedAssetType as AssetTypeId],
  };

  const { projectedValue, ageAtLtc } = useSelfFundingProjectedValue(previewState, {
    ltcAtYear: ltcAtYear ?? new Date().getFullYear(),
    yearsTillLtc: Math.max(yearsTillLtc ?? 0, 0),
    allPhaseCareMonthsNeeded: allPhaseCosts?.allPhaseCareMonthsNeeded,
    ltcAtAge: ltcAtAge ?? undefined,
    clientBirthDate: clientBirthDate ?? undefined,
  });

  const showPreview =
    flags.selfFundingGrowthPreview === true &&
    projectedValue != null &&
    selfFundingState != null;

  return {
    // Source / context
    selfFunding,
    partnerFirstName,
    clientName,

    // Form state
    selfFundingState,
    setSelfFundingState,
    hasContributionStopToggle,
    selectedAssetType,
    yearlyContributionValue,
    formErrors,
    formIsValid,
    hasRequiredFieldsErrors,

    // Allocation state
    mirrorChecked,
    setMirrorChecked,
    allocationOption,
    setAllocationOption,
    customClientPercent,
    setCustomClientPercent,

    // Modal overlays
    showWarning,
    showFormulaSheet,
    setShowFormulaSheet,

    // Derived
    isIncomeStream,
    isOther,
    isSpecificAssetType,
    isReadOnlyRecipient,
    isExistingMirror,
    fieldLabels,
    requiredKeys,
    incomeReferenceAgeError,
    showPartnerSection,
    showPreview,
    projectedValue,
    ageAtLtc,
    formulaInfo,
    formulaAssetTypeId,
    assetLabel,
    readOnlyAccountLabel,
    usedAccountTypes,
    totalAssets,
    flags,
    carePhaseYears,
    ltcEndYear,
    ltcAtYear,
    ltcAtAge,
    clientHasStartedLtc,
    careHasStarted,

    // Handlers
    handleChangeSelfFunding,
    handleChangeSelfFundingName,
    handleAssetTypeChange,
    handleToggleContributionStop,
    handleToggleDrawdown,
    handleChangeYearlyContribution,
    handleClickSubmit,
    onClose,
    handleConfirmClose,
    handleCancelClose,
  };
}

export type SelfFundingModalForm = ReturnType<typeof useSelfFundingModalForm>;
